/**
 * Verlassen-Warnung fuer Formulare mit ungespeicherten Eingaben.
 *
 * Ein Klick ins Menue oder auf einen Querverweis ersetzt die Seite - und
 * damit alles, was noch nicht gespeichert war. Solange `dirty` gilt, fragt
 * Canalis deshalb vor jedem Link innerhalb der Anwendung nach und der
 * Browser vor dem Schliessen oder Neuladen des Tabs. Links, die in einem
 * neuen Tab oeffnen, und Klicks mit Strg/Umschalt bleiben frei.
 *
 * Programmgesteuerte Wege (navigate nach Speichern oder Festschreiben)
 * laufen nicht hier durch - dafuer gibt es confirmLeave fuer die
 * Zurueck-Knoepfe.
 */

import { useEffect } from 'react';

const MESSAGE = 'Es gibt ungespeicherte Änderungen. Seite trotzdem verlassen? Die Eingaben gehen verloren.';

/** Fuer Knoepfe, die selbst navigieren: true = weiter, false = bleiben. */
export function confirmLeave(dirty: boolean): boolean {
  return !dirty || window.confirm(MESSAGE);
}

export function useUnsavedChanges(dirty: boolean): void {
  useEffect(() => {
    if (!dirty) return;

    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Aeltere Browser zeigen nur mit gesetztem returnValue einen Dialog.
      event.returnValue = '';
    };

    // Im Capture-Schritt, damit die Frage vor dem Router kommt: Ein
    // abgebrochener Klick ist fuer den Link "defaultPrevented" und wird
    // von react-router nicht mehr ausgefuehrt.
    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const target = event.target as Element | null;
      const anchor = target?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!anchor || anchor.target === '_blank' || anchor.hasAttribute('download')) return;
      if (anchor.origin !== window.location.origin) return;
      if (anchor.href === window.location.href) return;
      if (!window.confirm(MESSAGE)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    };

    window.addEventListener('beforeunload', onBeforeUnload);
    document.addEventListener('click', onClick, true);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('click', onClick, true);
    };
  }, [dirty]);
}
